export type Rgb8 = readonly [number, number, number];
export type Oklab = readonly [number, number, number];

/** Accepts "#rgb", "rgb", "#rrggbb" or "rrggbb" (case-insensitive). Throws Error on
 *  anything else (wrong length or non-hex characters). */
export function hexToRgb8(hex: string): Rgb8 {
  const h = hex.toLowerCase();
  const body = h.startsWith('#') ? h.slice(1) : h;
  const hexRe = /^[0-9a-f]+$/;
  if (!hexRe.test(body)) {
    throw new Error('Invalid hex colour: ' + hex);
  }
  if (body.length === 3) {
    const a = body.charAt(0);
    const b = body.charAt(1);
    const c = body.charAt(2);
    const r = parseInt(a + a, 16);
    const g = parseInt(b + b, 16);
    const bl = parseInt(c + c, 16);
    return [r, g, bl];
  }
  if (body.length === 6) {
    const r = parseInt(body.slice(0, 2), 16);
    const g = parseInt(body.slice(2, 4), 16);
    const bl = parseInt(body.slice(4, 6), 16);
    return [r, g, bl];
  }
  throw new Error('Invalid hex length: ' + hex);
}

/** Lower-case "#rrggbb". Channels are rounded and clamped to 0..255. */
export function rgb8ToHex(rgb: Rgb8): string {
  const toHex = (v: number): string => {
    const c = Math.max(0, Math.min(255, Math.round(v)));
    return c.toString(16).padStart(2, '0');
  };
  const r = rgb[0];
  const g = rgb[1];
  const b = rgb[2];
  if (r === undefined || g === undefined || b === undefined) {
    throw new Error('Incomplete RGB tuple');
  }
  return '#' + toHex(r) + toHex(g) + toHex(b);
}

/** sRGB transfer function, channel value 0..1 -> linear 0..1. */
export function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Inverse: l <= 0.0031308 ? l * 12.92 : 1.055 * l ** (1 / 2.4) - 0.055 */
export function linearToSrgb(l: number): number {
  return l <= 0.0031308 ? l * 12.92 : 1.055 * l ** (1 / 2.4) - 0.055;
}

/** sRGB 8-bit -> OKLab, using Björn Ottosson's reference matrices. */
export function rgb8ToOklab(rgb: Rgb8): Oklab {
  const r8 = rgb[0];
  const g8 = rgb[1];
  const b8 = rgb[2];
  if (r8 === undefined || g8 === undefined || b8 === undefined) {
    throw new Error('Incomplete RGB tuple');
  }
  const r = srgbToLinear(r8 / 255);
  const g = srgbToLinear(g8 / 255);
  const b = srgbToLinear(b8 / 255);
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const lC = Math.cbrt(l);
  const mC = Math.cbrt(m);
  const sC = Math.cbrt(s);
  const L = 0.2104542553 * lC + 0.793617785 * mC - 0.0040720468 * sC;
  const a = 1.9779984951 * lC - 2.428592205 * mC + 0.4505937099 * sC;
  const bl = 0.0259040371 * lC + 0.7827717662 * mC - 0.808675766 * sC;
  return [L, a, bl];
}

/** Euclidean distance in OKLab. */
export function oklabDistance(a: Oklab, b: Oklab): number {
  const a0 = a[0];
  const a1 = a[1];
  const a2 = a[2];
  const b0 = b[0];
  const b1 = b[1];
  const b2 = b[2];
  if (
    a0 === undefined || a1 === undefined || a2 === undefined ||
    b0 === undefined || b1 === undefined || b2 === undefined
  ) {
    throw new Error('Incomplete Oklab tuple');
  }
  const dx = a0 - b0;
  const dy = a1 - b1;
  const dz = a2 - b2;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/** Index of the palette entry nearest to `c`. Ties -> lowest index. Throws Error if the
 *  palette is empty. */
export function nearestPaletteIndex(c: Oklab, palette: readonly Oklab[]): number {
  if (palette.length === 0) {
    throw new Error('Palette is empty');
  }
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < palette.length; i++) {
    const entry = palette[i];
    if (entry === undefined) {
      continue;
    }
    const d = oklabDistance(c, entry);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}
